import React, { useState, useRef, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccessControl } from '../hooks/useAccessControl';
import SubscriptionPopup from './applicants/SubscriptionPopup';
import AppLoading from './AppLoading';

/**
 * ProductProtectedRoute - Route guard that checks for specific product access
 * Shows an unclosable pricing overlay if user lacks access
 * @param {string} requiredProduct - 'ats', 'tse', or 'bundle'
 * @param {ReactNode} children - Component to render if access is granted
 */
const ProductProtectedRoute = ({ children, requiredProduct }) => {
  const { currentUser } = useAuth();
  const { loading, productTypes } = useAccessControl();
  const location = useLocation();
  const [showPricingOverlay, setShowPricingOverlay] = useState(true);
  const tutorialEventRef = useRef(null);
  
  // Check if user has required product access
  const hasAccess = requiredProduct === 'ats' 
    ? (productTypes.includes('ats') || productTypes.includes('bundle'))
    : requiredProduct === 'tse'
    ? (productTypes.includes('tse') || productTypes.includes('bundle'))
    : productTypes.includes(requiredProduct);
  
  // Check if current page has a tutorial
  const hasTutorial = () => {
    const tutorialPages = [
      '/', // Home
      '/applicants', // Applicants
      '/sourcing', // TalentSearch
      '/employee-onboarding' // Onboarding
    ];
    return tutorialPages.includes(location.pathname);
  };

  // Handle tutorial button click
  const handleTutorialClick = () => {
    console.log('[ProductProtectedRoute] Tutorial button clicked');
    
    if (!hasTutorial()) {
      console.log('[ProductProtectedRoute] No tutorial available for this page');
      return;
    }
    
    setShowPricingOverlay(false);
    
    // Dispatch custom event to trigger tutorial on the page
    setTimeout(() => {
      const event = new CustomEvent('startTutorialFromOverlay', {
        detail: { 
          requiredProduct,
          onTutorialComplete: () => {
            console.log('[ProductProtectedRoute] Tutorial completed, checking access...');
            // After tutorial completes, check if user still lacks access
            if (!hasAccess) {
              console.log('[ProductProtectedRoute] User still lacks access, showing pricing overlay');
              setShowPricingOverlay(true);
            }
          }
        }
      });
      tutorialEventRef.current = event;
      window.dispatchEvent(event);
    }, 100);
  };
  
  // Reset state when location changes - MOVED BEFORE CONDITIONAL RETURNS
  useEffect(() => {
    if (!hasAccess) {
      setShowPricingOverlay(true);
    }
  }, [location.pathname, hasAccess]);
  
  // First check if user is authenticated
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  // Show loading state while checking access
  if (loading) {
    return <AppLoading />;
  }
  
  // If no access, show page with unclosable pricing overlay
  if (!hasAccess) {
    return (
      <div style={{ position: 'relative' }}>
        {/* Render the actual page content underneath */}
        {children}
        
        {/* Unclosable pricing overlay */}
        {showPricingOverlay && (
          <SubscriptionPopup 
            isOpen={true} 
            onClose={() => {}} // Empty function - makes it unclosable
            allowTutorialClose={hasTutorial()} // Only show tutorial button if page has tutorial
            onTutorialClick={handleTutorialClick}
          />
        )}
      </div>
    );
  }
  
  return children;
};

export default ProductProtectedRoute;
